import { useEffect, useState } from "react";
import { AlertTriangle, ShieldCheck, Sparkles, GraduationCap, Flame } from "lucide-react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import SectionTitle from "@/components/SectionTitle";

const ALERTS = [
  { t: "Fake Government Tax Refund SMS", d: "Impersonators claim refunds owed, harvesting bank details.", severity: "High" },
  { t: "AI Voice-Clone Grandparent Scam", d: "Cloned voices requesting emergency wire transfers.", severity: "Critical" },
  { t: "Investment Group Chat Fraud", d: "Encrypted 'stock tips' groups pumping worthless assets.", severity: "High" },
  { t: "Delivery Notification Phishing", d: "Fake courier links harvesting card CVV codes.", severity: "Medium" },
];

const TIPS = [
  "Never share OTPs or passwords, even with 'officials'.",
  "Hover over links to check the true destination.",
  "Enable multi-factor authentication everywhere.",
  "Search unknown numbers before returning missed calls.",
  "Confirm requests through a second, known channel.",
  "Report suspicious activity to national CERT / police.",
];

export default function Awareness() {
  const [trending, setTrending] = useState([]);
  const [total, setTotal] = useState(0);
  const [kind, setKind] = useState("");
  const [level, setLevel] = useState("");
  const [skip, setSkip] = useState(0);
  const LIMIT = 9;

  useEffect(() => {
    const params = { skip, limit: LIMIT };
    if (kind) params.kind = kind;
    if (level) params.level = level;
    api.get("/scams/trending", { params }).then((r) => {
      setTrending(r.data.items || []);
      setTotal(r.data.total || 0);
    }).catch(() => {});
  }, [kind, level, skip]);

  const KINDS = ["", "email", "sms", "text", "link", "pdf"];
  const LEVELS = ["", "Critical", "High"];

  const setFilter = (fn) => { setSkip(0); fn(); };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12" data-testid="awareness-page">
      <SectionTitle eyebrow="CRIME AWARENESS HUB" title="Alerts, prevention tips, and interactive learning." />

      {/* Trending Scams — live community-anonymised feed */}
      <div className="mt-12" data-testid="trending-scams-section">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs font-mono tracking-widest text-gold">TRENDING SCAMS · COMMUNITY FEED</div>
            <div className="font-display text-2xl mt-1">Anonymised, real submissions analysed by IOCRI AI</div>
          </div>
          <Flame className="h-6 w-6 text-gold hidden md:block" />
        </div>

        {/* Filters */}
        <div className="mt-6 flex flex-wrap gap-2 items-center" data-testid="trending-filters">
          <span className="text-[10px] font-mono tracking-widest text-white/50 mr-2">KIND</span>
          {KINDS.map((k) => (
            <button key={k || "all"} onClick={() => setFilter(() => setKind(k))} data-testid={`filter-kind-${k || "all"}`} className={`px-3 py-1 rounded-full text-[11px] font-mono tracking-widest border transition-colors ${kind === k ? "bg-gold text-black border-gold" : "border-white/10 text-white/60 hover:border-gold/40"}`}>
              {(k || "ALL").toUpperCase()}
            </button>
          ))}
          <span className="text-[10px] font-mono tracking-widest text-white/50 mx-2 ml-4">SEVERITY</span>
          {LEVELS.map((l) => (
            <button key={l || "all"} onClick={() => setFilter(() => setLevel(l))} data-testid={`filter-level-${l || "all"}`} className={`px-3 py-1 rounded-full text-[11px] font-mono tracking-widest border transition-colors ${level === l ? "bg-gold text-black border-gold" : "border-white/10 text-white/60 hover:border-gold/40"}`}>
              {(l || "ALL").toUpperCase()}
            </button>
          ))}
          <span className="ml-auto text-xs font-mono text-white/50">{total} results</span>
        </div>

        <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {trending.length === 0 && (
            <div className="col-span-full text-white/50 text-sm italic border border-white/5 rounded-2xl p-8 text-center">
              No scams match these filters yet. Submit one via <a href="/scam-detection" className="text-gold underline underline-offset-4">Scam Detection</a> to populate this feed.
            </div>
          )}
          {trending.map((s, i) => (
            <Card key={i} className="bg-obsidian-soft border-white/5 rounded-2xl" data-testid={`trending-${i}`}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <span className={`text-[10px] font-mono px-2 py-1 rounded-full ${s.risk_level === "Critical" ? "bg-red-500/20 text-red-300" : "bg-orange-500/20 text-orange-300"}`}>{(s.risk_level || "").toUpperCase()} · {s.risk_score}/100</span>
                  <span className="text-[10px] font-mono text-white/40">{(s.kind || "").toUpperCase()}</span>
                </div>
                <div className="mt-3 text-white/80 text-sm italic leading-snug">&ldquo;{s.excerpt}&rdquo;</div>
                {s.indicators?.length > 0 && (
                  <ul className="mt-3 space-y-1">
                    {s.indicators.slice(0, 3).map((it, j) => (
                      <li key={j} className="flex gap-2 text-xs text-white/60"><AlertTriangle className="h-3 w-3 text-orange-400 mt-0.5 shrink-0" />{it}</li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Pagination */}
        {total > LIMIT && (
          <div className="mt-6 flex items-center justify-center gap-3" data-testid="trending-pagination">
            <button disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - LIMIT))} data-testid="pg-prev" className="px-4 py-2 rounded-full text-xs font-mono tracking-widest border border-white/10 text-white/70 hover:border-gold/40 disabled:opacity-30 disabled:cursor-not-allowed">← PREV</button>
            <span className="text-xs font-mono text-white/50">PAGE {Math.floor(skip / LIMIT) + 1} / {Math.max(1, Math.ceil(total / LIMIT))}</span>
            <button disabled={skip + LIMIT >= total} onClick={() => setSkip(skip + LIMIT)} data-testid="pg-next" className="px-4 py-2 rounded-full text-xs font-mono tracking-widest border border-white/10 text-white/70 hover:border-gold/40 disabled:opacity-30 disabled:cursor-not-allowed">NEXT →</button>
          </div>
        )}
      </div>

      <div className="divider-gold mt-16" />

      <div className="mt-10 grid md:grid-cols-2 gap-4">
        {ALERTS.map((a, i) => (
          <Card key={i} className="bg-obsidian-soft border-white/5 rounded-2xl" data-testid={`alert-${i}`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-red-300 font-mono text-xs tracking-widest"><AlertTriangle className="h-4 w-4" /> LATEST SCAM ALERT</div>
                <span className={`text-[10px] font-mono px-2 py-1 rounded-full ${a.severity === "Critical" ? "bg-red-500/20 text-red-300" : a.severity === "High" ? "bg-orange-500/20 text-orange-300" : "bg-yellow-500/20 text-yellow-300"}`}>{a.severity.toUpperCase()}</span>
              </div>
              <div className="font-display text-xl mt-4">{a.t}</div>
              <div className="text-white/60 text-sm mt-2">{a.d}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="mt-16 grid lg:grid-cols-2 gap-4">
        <Card className="bg-obsidian-soft border-white/5 rounded-2xl">
          <CardContent className="p-6">
            <ShieldCheck className="h-6 w-6 text-gold" />
            <div className="font-display text-2xl mt-4">Crime Prevention Tips</div>
            <ul className="mt-4 space-y-2">
              {TIPS.map((t, i) => (<li key={i} className="flex gap-3 text-white/80"><Sparkles className="h-4 w-4 text-gold mt-1 shrink-0" />{t}</li>))}
            </ul>
          </CardContent>
        </Card>

        <Card className="bg-obsidian-soft border-white/5 rounded-2xl">
          <CardContent className="p-6">
            <GraduationCap className="h-6 w-6 text-gold" />
            <div className="font-display text-2xl mt-4">Interactive Quiz</div>
            <div className="text-white/60 text-sm mt-2">Test your scam-spotting instincts.</div>
            <div className="mt-6 rounded-xl border border-white/10 p-4">
              <div className="text-sm text-white/70">Q1. An email says your account will be closed today unless you &apos;verify&apos; via a link. What do you do?</div>
              <div className="mt-3 space-y-2 text-sm">
                {["Click and verify — better safe than sorry.", "Ignore. Log in via the official site if concerned.", "Reply asking who they are."].map((a, i) => (
                  <button key={i} data-testid={`quiz-opt-${i}`} className="w-full text-left px-3 py-2 rounded-lg border border-white/10 hover:border-gold/40 hover:text-gold text-white/80">{a}</button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
